import type {
  AssignShipmentHubOutcome,
  Shipment,
} from '@/services/api/shipments';

/** `shipments.type` as ops reads it. */
export const shipmentTypeLabel = (type: string | undefined): string | null => {
  switch (type) {
    case 'DOCKING':
      return 'Docking';
    case 'DIRECT_2W':
      return 'Direct 2W';
    case 'DIRECT_4W':
      return 'Direct 4W';
    default:
      return null;
  }
};

/** A partner shipment's handoverPoint, from its stored anchor role. */
export const handoverLabel = (
  anchorRole: Shipment['anchorRole']
): 'Delivery' | 'Retrieval' | null => {
  if (anchorRole === 'ORIGIN') return 'Delivery';
  if (anchorRole === 'DESTINATION') return 'Retrieval';
  return null;
};

/**
 * The role the Delivery / Retrieval tag shows: the list's
 * `effectiveAnchorRole` (own role, else the import's upload role), falling
 * back to the own `anchorRole` for an older BE that sends no effective one.
 * Display only — "Set hub" and the unresolved warning read `anchorRole`.
 */
export const displayAnchorRole = (
  s: Pick<Shipment, 'anchorRole' | 'effectiveAnchorRole'>
): Shipment['anchorRole'] => s.effectiveAnchorRole ?? s.anchorRole ?? null;

/**
 * A partner-API shipment whose hub/pitstop the API could not pick (no
 * candidate near it, or core was down) — ops must set it by hand (red warning
 * + bulk "Set hub"). Every handover but DOCKING + DELIVERY is resolved at
 * create; that one waits for the hub's inbound scan, so an empty hub is
 * normal there. Imports / H2H / 4W carry no handover and never qualify.
 */
export const needsHubResolve = (
  s: Pick<Shipment, 'status' | 'type' | 'anchorRole' | 'hubId'>
): boolean => {
  if (s.status !== 'CREATED' || s.hubId != null || s.anchorRole == null) {
    return false;
  }
  return !(s.type === 'DOCKING' && s.anchorRole === 'ORIGIN');
};

/**
 * A partner-API shipment ops may (re)set the hub/pitstop of with "Set hub":
 * unresolved (CREATED), or resolved and still at the hub (AT_HUB) — moving it
 * to another one. DOCKING + DELIVERY never: its hub is where the goods were
 * scanned in. The BE has the last word (items on a rider, an open dispatch
 * preview, …) and answers per shipment.
 */
export const canSetHub = (
  s: Pick<Shipment, 'status' | 'type' | 'anchorRole'>
): boolean => {
  if (s.anchorRole == null) return false;
  if (s.status !== 'CREATED' && s.status !== 'AT_HUB') return false;
  return !(s.type === 'DOCKING' && s.anchorRole === 'ORIGIN');
};

/** Why "Set hub" left a shipment out — the BE outcome, in ops' words. */
export const HUB_SKIP_REASON: Record<
  Exclude<AssignShipmentHubOutcome, 'assigned' | 'reassigned'>,
  string
> = {
  not_found: 'tidak ditemukan',
  status_locked: 'sudah bukan CREATED / AT_HUB',
  no_handover: 'bukan shipment API (tanpa handoverPoint)',
  waits_for_scan: 'Docking + Delivery — hub diisi saat scan inbound',
  scanned_at_hub: 'Docking + Delivery — barang sudah di-scan di hub-nya',
  items_locked: 'item sudah di rider / delivery-service',
  in_dispatch_preview: 'ada di preview dispatch yang masih terbuka',
  same_hub: 'sudah di hub ini',
  other_client_pitstop: 'pitstop milik client lain',
};
